import React, {useEffect, useImperativeHandle, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import {useAuthorize} from 'components/organisms/Providers/AuthProvider/AuthProvider';

import ActionSwitch from 'components/molecules/Switches/ActionSwitch/ActionSwitch';
import TasksList from 'components/organisms/Lists/TasksList/TasksList';
import {useCommentToggleTaskDone} from 'services/comment/comment.utils';
import {useCommentList} from 'services/comment/comment.hooks';
import constants from 'helpers/constants';
import utils from 'helpers/utils';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import {useProfile} from 'components/organisms/Providers/ProfileProvider/ProfileProvider';
import StyledCollectionEntitiesTasksGraphCard
  from 'components/organisms/Cards/CollectionEntitiesTasksGraphCard/CollectionEntitiesTasksGraphCard.styles';
import Box from 'components/atoms/Layout/Box/Box';
import {P} from 'components/atoms/Text/Typography/Typography';

const CollectionEntitiesTasksGraphCard = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'CollectionEntitiesTasksGraphCard', {
    children: ['tasks', 'stepper']
  });

  const innerRef = useRef(null);
  const authorize = useAuthorize();
  const tableProvider = useTable();
  const profileProvider = useProfile();
  const collection = tableProvider.context?.data;
  const graphState = tableProvider.graphState;

  useImperativeHandle(ref, () => innerRef.current);

  const showMyTasks = Boolean(graphState.graphSettings['openTasks']?.filter?.showMyTasks);
  const showDueTasks = Boolean(graphState.graphSettings['openTasks']?.filter?.showDueTasks);
  const entityListState = useEffectItem(tableProvider.appliedListState());

  const tasks = useCommentList({
    page: 0,
    pageSize: 10,
    filter: (showMyTasks ? [{id: 'responsible', value: true}] : [])
      .concat(showDueTasks ? [{id: 'due', value: true}] : [])
      .concat([{id: 'open', value: true}, {id: 'task', value: true}, {
        id: 'entityFilter',
        value: {
          collectionId: collection?.collectionId,
          ...utils.filter2Object(entityListState.filter),
          search: entityListState.search
        }
      }]),
    sort: showDueTasks ? [{id: 'dueDate', desc: true}] : [{id: 'createdAt', desc: true}]
  }, {
    ...constants.queryOptions.infinite,
    enabled: collection?.collectionId > 0
  });

  const setGraphSettingsEvent = useEffectEvent(graphState.setGraphSettings);
  const switchActions = useMemo(() => {
    return [
      {
        label: 'My tasks',
        active: showMyTasks,
        onClick: (e) => {
          setGraphSettingsEvent?.((current) => ({
            ...current,
            'openTasks': {
              ...current?.openTasks,
              filter: {
                ...current?.openTasks?.filter,
                showMyTasks: !showMyTasks
              }
            }
          }));
          e.preventDefault();
        },
      },
      {
        label: 'Due tasks',
        active: showDueTasks,
        onClick: (e) => {
          setGraphSettingsEvent?.((current) => ({
            ...current,
            'openTasks': {
              ...current?.openTasks,
              filter: {
                ...current?.openTasks?.filter,
                showDueTasks: !showDueTasks
              }
            }
          }));
          e.preventDefault();
        },
      }
    ]
  }, [setGraphSettingsEvent, showMyTasks, showDueTasks]);

  useEffect(() => {
    const scroll = () => {
      return innerRef.current?.querySelector('.DataList-list')?.scrollTo({top: 0, left: 0});
    }

    return utils.observeRetry(scroll, 5);
  }, [showMyTasks, showDueTasks]);

  const renderActionSwitches = () => {
    return <React.Fragment>
      {switchActions.map((action, idx) => {
        return <ActionSwitch key={idx} action={action}
                             SwitchProps={{
                               size: 'medium',
                               color: 'primary'
                             }}/>
      })}
    </React.Fragment>
  }

  const toggleDone = useCommentToggleTaskDone();
  const taskCardProps = useMemo(() => ({
    onToggle: (task, done) => {
      return toggleDone?.(task, done);
    },
    onCanUpdate: (task, markDone = false) => {
      return authorize({
        attribute: markDone ? 'task.markDone' : 'task.update',
        meta: {task}
      });
    },
    orientation: 'horizontal'
  }), [authorize, toggleDone]);

  const handleClick = (e, task) => {
    if (!e.defaultPrevented) {
      profileProvider.openTask(task.commentId);
    }
  }

  const taskCount = utils.formatNumber(tasks.meta?.resultsCount ?? 0);
  const isLoading = tasks.status.isLoading || !utils.isDefined(tasks.data);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledCollectionEntitiesTasksGraphCard ref={innerRef} {...innerProps}
                                                 title={(isLoading ? 'Tasks' : `${taskCount ?? 0} task${(tasks.meta?.resultsCount ?? 0) === 1 ? '' : 's'}`)}
                                                 context={renderActionSwitches()}
                                                 isLoading={tableProvider.isLoading() || isLoading}>
    {(tasks.data?.length > 0 || isLoading) ?
      <TasksList className="CollectionEntitiesTasksGraphCard-tasks"
                 tasks={tasks}
                 onClick={handleClick}
                 isLoading={isLoading}
                 TaskCardProps={taskCardProps} /> : null}

    {(tasks.data?.length === 0 && !isLoading) ?
      <Box className="GraphCard-empty">
        <P>No task(s) found</P>
      </Box> : null}
  </StyledCollectionEntitiesTasksGraphCard>
});

CollectionEntitiesTasksGraphCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
};

export default CollectionEntitiesTasksGraphCard;
